import type {
  BookingDetailsData,
  BookingDetailsParticipantType,
  BookingDocument,
  BookingPreference,
  ExcursionsData,
  FullCancellationCostsData,
  PartialCancellationFeeData,
  PartialCancellationRequestPayload,
  TransfersData,
} from '../domain/types'
import defaultFetch from 'isomorphic-unfetch'
import { BookingNotFoundError } from '../domain/errors'
import { isVoucherCancellationUrlValid, VOUCHER_CANCELLATION_PATHNAME } from '../domain/policies/voucherCancellationUrl'
import { bookingDetailsMapper } from './mappers/bookingDetailsMapper'
import { bookingPreferencesMapper } from './mappers/bookingPreferencesMapper'
import { bookingPreferencesPayloadMapper } from './mappers/bookingPreferencesPayloadMapper'
import { documentsMapper } from './mappers/documentsMapper'
import { excursionsMapper } from './mappers/excursionsMapper'
import { fullCancellationCostsMapper } from './mappers/fullCancellationCostsMapper'
import { partialCancellationFeeMapper } from './mappers/partialCancellationFeeMapper'
import { participantEditRulesMapper } from './mappers/participantEditRulesMapper'
import { participantEditSubmitMapper } from './mappers/participantEditSubmitMapper'
import { createSpecialLuggagePayloadMapper } from './mappers/specialLuggagePayloadMapper'
import { transfersMapper } from './mappers/transfersMapper'
import {
  BookingDetailsAPIResponse,
  BookingDocumentsAPIResponse,
  BookingPreferencesAPIResponse,
  CancellationCostsAPIResponse,
  CancellationSubmitAPIResponse,
  ExcursionsAPIResponse,
  ParticipantEditPayload,
  SetBookingPreferencesAPIResponse,
  TransfersAPIResponse,
} from './types'

const PARTICIPANT_EDIT_SUBMIT_TIMEOUT_MS = 60_000

function timeoutSignal(milliseconds: number): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(milliseconds)
  }

  const controller = new AbortController()
  setTimeout(() => controller.abort(), milliseconds)

  return controller.signal
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
    NEXT_PUBLIC_API_URL: string
  }
  fetch: typeof defaultFetch
  contextItemId: string
}

export function createBookingDetailsApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    },
    fetch = defaultFetch,
    contextItemId,
  } = dependencies as Dependencies

  const specialLuggagePayloadMapper = createSpecialLuggagePayloadMapper()

  function getRequiredApiBaseUrl(): string {
    const apiBaseUrl = env.NEXT_PUBLIC_API_BASE_URL
    if (!apiBaseUrl) {
      throw new Error('Missing NEXT_PUBLIC_API_BASE_URL')
    }

    return apiBaseUrl
  }

  async function getDocumentsData(bookingNumber: string): Promise<BookingDocument[]> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingsDocumentsApi/GetDocuments?bookingNumber=${bookingNumber}&contextitemid=${contextItemId}`,
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch booking documents: ${response.status}`)
    }

    const data: BookingDocumentsAPIResponse = await response.json()

    if (data.isValid === false) {
      throw new Error('Invalid booking documents')
    }

    return documentsMapper(data)
  }

  async function getBookingDetailsData(bookingNumber: string): Promise<BookingDetailsData> {
    const params = new URLSearchParams({ bookingNumber, contextItemId })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/GetBookingDetail?${params}`,
    )
    if (response.status === 404)
      throw new BookingNotFoundError()
    if (!response.ok)
      throw new Error(`Failed to fetch booking data: ${response.status}`)
    const data: BookingDetailsAPIResponse = await response.json()
    return bookingDetailsMapper(data)
  }

  async function getExcursionsData(
    latitude: string,
    longitude: string,
    startDate: string,
    endDate: string,
  ): Promise<ExcursionsData> {
    const params = new URLSearchParams({ latitude, longitude, startDate, endDate })
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/ExcursionsApi/GetExcursions?${params}`
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch excursions: ${response.status}`)
    }

    const data: ExcursionsAPIResponse = await response.json()
    return excursionsMapper(data)
  }

  async function getTransfersData(bookingNumber: string): Promise<TransfersData> {
    const params = new URLSearchParams({ bookingNumber, contextitemid: contextItemId })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/TransfersApi/GetTransfers?${params}`,
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch transfers: ${response.status}`)
    }

    const data: TransfersAPIResponse = await response.json()
    return transfersMapper(data)
  }

  async function deleteBooking(bookingNumber: string, customerId: number): Promise<void> {
    const params = new URLSearchParams({ bookingNumber, customerId: String(customerId) })
    const response = await fetch(`${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/DeleteBookingFromAccount?${params}`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error(`Failed to delete booking: ${response.status}`)
    }
  }

  async function updateRebookingNumber(bookingNumber: string): Promise<void> {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/UpdateDefaultReBookingNumber?contextitemid=${contextItemId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ bookingNumber }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to update rebooking number: ${response.status}`)
    }

    // TODO: handle isValid from response { isValid: boolean }
  }

  async function getCancellationCostsResponse(
    bookingNumber: string,
    errorMessages: { noContent: string, fetchFailed: string },
  ): Promise<CancellationCostsAPIResponse> {
    const params = new URLSearchParams({ bookingNumber })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/GetCancellationCosts?${params}`,
    )

    if (response.status === 204) {
      throw new Error(errorMessages.noContent)
    }

    if (!response.ok) {
      throw new Error(`${errorMessages.fetchFailed}: ${response.status}`)
    }

    return response.json()
  }

  async function getPartialCancellationFeeData(bookingNumber: string): Promise<PartialCancellationFeeData> {
    const data = await getCancellationCostsResponse(bookingNumber, {
      noContent: 'No partial cancellation fee data',
      fetchFailed: 'Failed to fetch partial cancellation fee',
    })
    return partialCancellationFeeMapper(data)
  }

  async function getFullCancellationCostsData(bookingNumber: string): Promise<FullCancellationCostsData> {
    const data = await getCancellationCostsResponse(bookingNumber, {
      noContent: 'No full cancellation costs data',
      fetchFailed: 'Failed to fetch full cancellation costs',
    })
    return fullCancellationCostsMapper(data)
  }

  async function submitSpecialLuggage(bookingNumber: string, formData: Record<string, unknown>): Promise<void> {
    const params = new URLSearchParams({ bookingNumber, contextitemid: contextItemId })
    const payload = (() => {
      try {
        return specialLuggagePayloadMapper.map(formData)
      }
      catch {
        // Fallback keeps the request flow working for luggage types not yet covered by mapper.
        return formData
      }
    })()

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/SendSpecialLuggageRequestAsync?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to submit special luggage request: ${response.status}`)
    }
  }

  async function submitPartialCancellationRequest(
    bookingNumber: string,
    payload: PartialCancellationRequestPayload,
  ): Promise<void> {
    const params = new URLSearchParams({ bookingNumber })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/PartialCancellationRequestContactCenter?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to submit partial cancellation request: ${response.status}`)
    }
  }

  async function getBookingPreferences(bookingNumber: string): Promise<BookingPreference[]> {
    const params = new URLSearchParams({ bookingNumber })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/GetBookingPreferences?${params}`,
    )

    if (!response.ok)
      throw new Error(`Failed to fetch booking preferences: ${response.status}`)

    const data: BookingPreferencesAPIResponse = await response.json()
    return bookingPreferencesMapper(data)
  }

  async function setBookingPreferences(
    bookingNumber: string,
    preferences: BookingPreference[],
  ): Promise<BookingPreference[] | null> {
    const params = new URLSearchParams({ bookingNumber })
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/BookingApi/SetBookingPreferences?${params}`,
      {
        method: 'PATCH',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingPreferencesPayloadMapper(preferences)),
      },
    )

    if (!response.ok)
      throw new Error(`Failed to save booking preferences: ${response.status}`)

    let data: SetBookingPreferencesAPIResponse | undefined
    try {
      data = await response.json()
    }
    catch {
      return null
    }
    return data?.preferences ? bookingPreferencesMapper(data.preferences) : null
  }

  async function getParticipantEditRules(
    contactDomainCode: string,
    bookingNumber: string,
    participantId: string,
    participantType: BookingDetailsParticipantType,
  ) {
    const params = new URLSearchParams({ type: participantType, contextItemId })
    const response = await fetch(
      `/${contactDomainCode}/selfservice/bookings/${bookingNumber}/rules/participants/${participantId}/with-configuration?${params}`,
      {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch participant edit rules: ${response.status}`)
    }

    return participantEditRulesMapper(await response.json())
  }

  async function submitParticipantEdit(
    contactDomainCode: string,
    bookingNumber: string,
    participantId: string,
    payload: ParticipantEditPayload,
  ): Promise<string | undefined> {
    const response = await fetch(
      `/${contactDomainCode}/selfservice/bookings/${bookingNumber}/participants/${participantId}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: timeoutSignal(PARTICIPANT_EDIT_SUBMIT_TIMEOUT_MS),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to submit participant edit: ${response.status}`)
    }

    return participantEditSubmitMapper(await response.json())
  }

  async function submitFullCancellation(bookingNumber: string, isWithCost: boolean): Promise<void> {
    const params = new URLSearchParams({
      bookingNumber,
      isWithCost: String(isWithCost),
    })
    const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/BookingApi/CancelBooking?${params}`, {
      method: 'DELETE',
      credentials: 'include',
      referrerPolicy: 'no-referrer-when-downgrade',
    })

    if (!response.ok) {
      throw new Error(`Failed to submit full cancellation: ${response.status}`)
    }

    const data: CancellationSubmitAPIResponse = await response.json()

    if (data.isValid !== true) {
      throw new Error('Invalid full cancellation response')
    }
  }

  function buildVoucherCancellationRequestUrl(voucherCancellationUrl: string): string {
    if (!isVoucherCancellationUrlValid(voucherCancellationUrl))
      throw new Error('Invalid voucher cancellation URL')

    const apiOrigin = new URL(getRequiredApiBaseUrl()).origin
    const requestUrl = new URL(voucherCancellationUrl.trim(), apiOrigin)

    if (requestUrl.origin !== apiOrigin || requestUrl.pathname !== VOUCHER_CANCELLATION_PATHNAME) {
      throw new Error('Invalid voucher cancellation URL')
    }

    return requestUrl.href
  }

  async function submitVoucherCancellation(voucherCancellationUrl: string): Promise<void> {
    const response = await fetch(buildVoucherCancellationRequestUrl(voucherCancellationUrl), {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`Failed to submit voucher cancellation: ${response.status}`)
    }

    const data: CancellationSubmitAPIResponse = await response.json()

    if (data.isValid !== true) {
      throw new Error('Invalid voucher cancellation response')
    }
  }

  return {
    getDocumentsData,
    getBookingDetailsData,
    getExcursionsData,
    getTransfersData,
    deleteBooking,
    updateRebookingNumber,
    getPartialCancellationFeeData,
    getFullCancellationCostsData,
    submitSpecialLuggage,
    submitPartialCancellationRequest,
    getBookingPreferences,
    setBookingPreferences,
    getParticipantEditRules,
    submitParticipantEdit,
    submitFullCancellation,
    submitVoucherCancellation,
  }
}

export default createBookingDetailsApiClient()
